'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface TeamMember {
  name: string;
  role: string;
  description: string;
  image: string;
  alt: string;
  gradient: string;
  path: string;
}

const TeamSection = () => {
  const router = useRouter();
  const { isAuthenticated } = useAuth();

  const team: TeamMember[] = [
    {
      name: 'Gen-IA',
      role: 'Coach de Lluvia de Ideas',
      description: 'Especialista en generación de ideas innovadoras, expansión de conceptos creativos y eliminación de bloqueos mentales en las primeras etapas de ideación.',
      image: '/assets/images/genia_lluvia_ideas-1770283270530.png',
      alt: 'Gen-IA - Coach de lluvia de ideas especializado en creatividad y generación de conceptos',
      gradient: 'from-purple-500 to-pink-600',
      path: '/brainstorm-coach',
    },
    {
      name: 'SocrIAtes',
      role: 'Coach de Pensamiento Paralelo',
      description: 'Experto en pensamiento lateral y disruptivo, desafía suposiciones y replantea problemas desde ángulos innovadores para encontrar soluciones estratégicas.',
      image: '/assets/images/socriates_pensamiento_paralelo-1770284247284.png',
      alt: 'SocrIAtes - Coach de pensamiento paralelo para análisis lateral y estratégico',
      gradient: 'from-blue-500 to-indigo-600',
      path: '/obx',
    },
    {
      name: 'AI-de',
      role: 'Amplificador de Ideas',
      description: 'Transforma ideas iniciales en propuestas sólidas, añadiendo estructura, profundidad y escalabilidad a tus conceptos empresariales.',
      image: '/assets/images/aide_ideamaker-1770282880344.png',
      alt: 'AI-de - Amplificador de ideas para estructurar y mejorar conceptos de negocio',
      gradient: 'from-green-500 to-emerald-600',
      path: '/ai-idea-coach',
    },
    {
      name: 'AI Coach',
      role: 'Coach de Startups y Negocios',
      description: 'Mentor general especializado en validación empresarial, orientación estratégica y soporte en decisiones críticas con mentalidad de fundador experimentado.',
      image: '/assets/images/AI_coach-1770284266834.png',
      alt: 'AI Coach - Coach general de startups para validación y estrategia empresarial',
      gradient: 'from-orange-500 to-amber-600',
      path: '/ai-business-coach',
    },
  ];

  const handleMemberClick = (path: string) => {
    if (isAuthenticated) {
      router.push(path);
      return;
    }

    const redirectParam = encodeURIComponent(path);
    router.push(`/register?redirect=${redirectParam}`);
  };

  return (
    <section className="relative overflow-hidden py-20 lg:py-32 bg-[radial-gradient(circle_at_top_left,rgba(44,120,251,0.10),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(62,224,190,0.14),transparent_32%),linear-gradient(180deg,#f6fbff_0%,#ffffff_48%,#eefcf8_100%)]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-5rem] top-12 h-72 w-72 rounded-full bg-[rgba(44,120,251,0.12)] blur-3xl" />
        <div className="absolute bottom-[-4rem] right-[-4rem] h-80 w-80 rounded-full bg-[rgba(62,224,190,0.14)] blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="section-eyebrow mb-6">
            <Icon name="UserGroupIcon" size={18} className="text-[var(--primary)]" />
            <span>Nuestro Equipo</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
            Conoce a tus{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'var(--gradient-app)' }}>
              Mentores de IA
            </span>
          </h2>

          <p className="text-xl text-gray-600 leading-relaxed">
            Nuestro equipo está formado por mentores de inteligencia artificial especializados, cada uno diseñado para acompañarte en una fase específica de tu viaje emprendedor.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {team.map((member, index) => (
            <div
              key={index}
              className="group app-card p-8 hover:shadow-2xl transition-all duration-300 border border-[rgba(44,120,251,0.12)] hover:border-[rgba(44,120,251,0.28)] hover:-translate-y-1"
            >
              <div className="relative mb-6">
                <div className={`absolute inset-0 bg-gradient-to-br ${member.gradient} rounded-2xl opacity-20 blur-xl group-hover:opacity-30 transition-opacity duration-300`}></div>
                <div className="relative w-full h-48 sm:h-52 rounded-2xl overflow-hidden shadow-md">
                  <AppImage
                    src={member.image}
                    alt={member.alt}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                {member.name}
              </h3>

              <p className="text-sm font-semibold text-[var(--primary-strong)] mb-4 text-center">
                {member.role}
              </p>

              <p className="text-gray-600 leading-relaxed text-center mb-6">
                {member.description}
              </p>

              <button
                type="button"
                onClick={() => handleMemberClick(member.path)}
                className="btn-primary-app w-full inline-flex items-center justify-center px-5 py-3"
              >
                Trabajar con {member.name}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
